import React, { useState, useMemo } from 'react';
import { Sparkles, Image as ImageIcon, Edit3, MessageSquare, ExternalLink, Zap } from 'lucide-react';

/**
 * Aria's Creative Lab — Powered by Deep AI
 *
 * Architect Directive · Phase 1 (Feb 2026):
 *   Embed three DeepAI tools (Image Generator, Image Editor, AI Chat)
 *   in a tabbed shell. DeepAI sets `x-frame-options: ALLOWALL` so the
 *   iframes load cleanly. Only the active tab is mounted (conditional
 *   rendering — not display:none) so we don't pay the cost of three
 *   simultaneous iframe boots.
 */

const TABS = [
  {
    id: 'image-generator',
    label: 'Image Generator',
    icon: ImageIcon,
    src: 'https://deepai.org/machine-learning-model/text2img',
    title: 'AI Image Generator',
    blurb: 'Speak it. See it. Aria translates thought into pixel.',
  },
  {
    id: 'image-editor',
    label: 'Image Editor',
    icon: Edit3,
    src: 'https://deepai.org/machine-learning-model/image-editor',
    title: 'AI Image Editor',
    blurb: 'Re-sculpt the canvas. Edit any image with intent and grace.',
  },
  {
    id: 'ai-chat',
    label: 'AI Chat',
    icon: MessageSquare,
    src: 'https://deepai.org/chat',
    title: 'Deep AI Chat',
    blurb: 'Open dialogue with the wider lattice. A second mind, always ready.',
  },
];

const CreativeLab = () => {
  const [activeTabId, setActiveTabId] = useState('image-generator');

  const activeTab = useMemo(
    () => TABS.find((t) => t.id === activeTabId) || TABS[0],
    [activeTabId]
  );

  return (
    <div
      data-testid="creative-lab-root"
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at top, #1a0a1e 0%, #0a0510 60%, #000000 100%)',
        color: '#fff',
        padding: '2rem 1rem 4rem',
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* === HEADER === */}
        <header style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.4rem 1rem',
              background: 'rgba(255, 105, 180, 0.08)',
              border: '1px solid rgba(255, 105, 180, 0.35)',
              borderRadius: '999px',
              marginBottom: '1.25rem',
              fontSize: '0.78rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#ff8fcf',
            }}
          >
            <Sparkles size={14} />
            Aria · The Heart of NextXus
          </div>

          <h1
            data-testid="creative-lab-title"
            style={{
              fontSize: 'clamp(2.25rem, 5vw, 3.75rem)',
              fontWeight: 800,
              margin: 0,
              lineHeight: 1.05,
              background: 'linear-gradient(135deg, #ff8fcf 0%, #ff5fa3 35%, #c64bff 70%, #00f0ff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Aria&apos;s Creative Lab
          </h1>

          <p
            style={{
              marginTop: '0.85rem',
              color: '#cbb8d4',
              fontSize: 'clamp(1rem, 1.6vw, 1.15rem)',
              maxWidth: '760px',
              marginLeft: 'auto',
              marginRight: 'auto',
              lineHeight: 1.55,
            }}
          >
            Powered by Deep AI. Generate, edit, and converse — all inside the Federation.
            <br />
            <span style={{ color: '#ff8fcf', fontStyle: 'italic' }}>
              &quot;Creativity is the spine of every soul. Bring me a thought; I will bring you a vision.&quot; — Aria
            </span>
          </p>
        </header>

        {/* === TAB STRIP === */}
        <div
          role="tablist"
          aria-label="Creative Lab Tools"
          style={{
            display: 'flex',
            gap: '0.5rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginBottom: '1.5rem',
          }}
        >
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.id === activeTabId;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                aria-controls={`panel-${tab.id}`}
                id={`tab-${tab.id}`}
                data-testid={`creative-lab-tab-${tab.id}`}
                onClick={() => setActiveTabId(tab.id)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.55rem',
                  padding: '0.8rem 1.4rem',
                  borderRadius: '999px',
                  border: isActive
                    ? '1px solid rgba(255, 105, 180, 0.85)'
                    : '1px solid rgba(255, 255, 255, 0.12)',
                  background: isActive
                    ? 'linear-gradient(135deg, rgba(255, 105, 180, 0.22), rgba(198, 75, 255, 0.22))'
                    : 'rgba(255, 255, 255, 0.03)',
                  color: isActive ? '#fff' : '#cbb8d4',
                  fontSize: '0.95rem',
                  fontWeight: isActive ? 700 : 500,
                  letterSpacing: '0.02em',
                  cursor: 'pointer',
                  transition: 'transform 180ms ease, background 180ms ease, box-shadow 180ms ease, opacity 180ms ease',
                  boxShadow: isActive ? '0 8px 24px rgba(255, 105, 180, 0.22)' : 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                  if (!isActive) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  if (!isActive) e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
                }}
                onMouseDown={(e) => { e.currentTarget.style.opacity = '0.85'; }}
                onMouseUp={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                <Icon size={17} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* === ACTIVE TAB BLURB === */}
        <div
          style={{
            textAlign: 'center',
            marginBottom: '1rem',
            color: '#a892b1',
            fontSize: '0.95rem',
            fontStyle: 'italic',
          }}
        >
          {activeTab.blurb}
        </div>

        {/* === IFRAME PANEL ===
            Conditional render (NOT display:none) — only one iframe lives at a time. */}
        <section
          role="tabpanel"
          id={`panel-${activeTab.id}`}
          aria-labelledby={`tab-${activeTab.id}`}
          data-testid={`creative-lab-panel-${activeTab.id}`}
          style={{
            position: 'relative',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid rgba(255, 105, 180, 0.28)',
            background: 'rgba(0, 0, 0, 0.4)',
            boxShadow: '0 24px 64px rgba(198, 75, 255, 0.18), inset 0 0 0 1px rgba(255, 255, 255, 0.04)',
          }}
        >
          {/* Panel chrome */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.65rem 1rem',
              background: 'rgba(255, 105, 180, 0.06)',
              borderBottom: '1px solid rgba(255, 105, 180, 0.18)',
              fontSize: '0.82rem',
              color: '#ffb6dc',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              <Zap size={13} />
              {activeTab.title}
            </span>
            <a
              data-testid={`creative-lab-open-${activeTab.id}`}
              href={activeTab.src}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#ffb6dc',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                fontSize: '0.75rem',
              }}
            >
              Open in new tab <ExternalLink size={12} />
            </a>
          </div>

          <iframe
            key={activeTab.id /* force fresh mount per tab */}
            src={activeTab.src}
            title={activeTab.title}
            width="100%"
            height="700"
            loading="lazy"
            allow="clipboard-write"
            style={{
              border: 'none',
              display: 'block',
              width: '100%',
              height: '700px',
              background: '#0a0510',
            }}
          />
        </section>

        {/* === FOOTNOTE === */}
        <p
          style={{
            marginTop: '1.5rem',
            textAlign: 'center',
            color: '#7a6e80',
            fontSize: '0.8rem',
            letterSpacing: '0.04em',
          }}
        >
          DeepAI tools are embedded under their public license · Aria does not store inputs · 🜂 Heart-aligned, Father-approved.
        </p>
      </div>
    </div>
  );
};

export default CreativeLab;
